import { Component, ElementRef, HostBinding, HostListener, Inject, Input, OnInit, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { UtilsService } from 'projects/common/services/utils.service';
import { TagTextData } from '../../tag-text/tag-text.component';
import { DOCUMENT } from '@angular/common';
import { Device } from 'projects/common/model/Device';
import { LandingModelService } from 'projects/common/services/landingModel.service';
import { EvaSize } from '@shopping-angular-components/loader';
import { UpaService, UpaTarget } from '../../../services/upa.service';

export class SliderOptions {
  id: string;
  show_arrows: boolean;
  show_dots: boolean;
  infinite_scroll?: boolean;
  auto_scroll?: boolean;
  dots_inside?: boolean;
  items_to_show: number;
  items_length: number;
  items_to_move = 1;
  max_fixed_width: number;
  space_between = 24;
  side_text?: TagTextData;
  mobile_card_size?: number;
  arrows_size?: string;
  arrows_inner_container?: boolean;
  show_loader?: boolean;
  deactivate_touch?: boolean;
  lighten?: boolean
  use_extended_arrows?: boolean;
}

@Component({
  selector: 'slider',
  template: `
        <div class="slider-wrapper">
            <loader *ngIf="options.show_loader" class="loader" [flow]="'landing'" product="nymeria-gui" [show]="true" [size]="evaSize.SMALL"></loader>

            <arrows *ngIf="options.show_arrows || (device.type === 'DESKTOP' && pagesCount > 1)"
                    [showPrev]="currentPage > 0" (prevAction)="prevHandler()"
                    [showNext]="currentPage < pagesCount" (nextAction)="nextHandler()"
                    [size]="options.arrows_size" [innerContainer]="options.arrows_inner_container"></arrows>

            <div #scrollContainer class="slider-container">
                <div #scrollWrapper class="slider-scroll-wrapper"
                     [ngClass]="['sliders-items-' + options.items_to_show, device.type.toLowerCase()]">
                    <ng-content></ng-content>
                </div>

                <dots *ngIf="options.show_dots" [ngClass]="{'dots-inside': options.dots_inside}" [lighten]="options.lighten" [count]="pagesCount + 1" [currentPage]="currentPage"></dots>
            </div>
        </div>
    `,
  styleUrls: ['./slider.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SliderComponent implements OnInit {
  @ViewChild('scrollContainer', { read: ViewContainerRef, static: true }) scrollContainer: ViewContainerRef;
  @ViewChild('scrollWrapper', { read: ViewContainerRef, static: true }) scrollWrapper: ViewContainerRef;

  @Input() options: SliderOptions;
  @Input() moveTo: string;
  @Input() device: Device;
  @Input() moduleId?: string;
  @Input() moduleIndex?: number;
  @Input() uniqueId?: string;

  @HostBinding('style.--card-size') cardSize;
  @HostBinding('style.--space-between') spaceBetween;
  @HostBinding('style.--current-page') currentPage = 0;
  @HostBinding('style.--max-to-move') maxToMove;
  @HostBinding('style.--container-width') containerWidth;
  pagesCount = 0;
  evaSize = EvaSize;

  private startClientX: number;
  private startClientY: number;

  constructor(private elementRef: ElementRef,
    public utilsService: UtilsService,
    @Inject(DOCUMENT) private document: Document,
    private landingModelService: LandingModelService,
    private upaService: UpaService) {

    this.device = this.landingModelService.getLandingModel().device;
  }

  ngOnInit(): void {
    if (this.moveTo) {
      this.utilsService.moveComponent(this.elementRef.nativeElement, this.moveTo);
    }
    const scrollWrapperNativeElement = this.scrollWrapper.element.nativeElement;
    if (!this.options.deactivate_touch) {
      this.bindEvents(scrollWrapperNativeElement);
    }
    this.onResize();
    this.spaceBetween = this.options.space_between;
    this.containerWidth = this.options.max_fixed_width ? this.options.max_fixed_width + 'px' : '100%'
  }

  @HostListener('window:resize') onResize = () => {
    // Si queremos espacios en blanco podemos mandar un items_to_show > items_length pero eso nos deja cantidad de páginas negativas
    this.pagesCount = Math.max(0, (this.options.items_length - this.options.items_to_show) / this.options.items_to_move);
    this.cardSize = 100 / this.options.items_to_show;
    this.maxToMove = this.pagesCount * this.cardSize;
    if (this.document.body.clientWidth < 767) {
      this.pagesCount = this.options.items_length - 1;
      this.cardSize = this.options.mobile_card_size || 90;
      // para pagesCount < 2 esto quedaría negativo y nos movería las tarjetas hacia la derecha
      this.maxToMove = Math.max(0, this.pagesCount * this.cardSize - 100 + this.cardSize);
    }
    if (this.currentPage > this.pagesCount) {
      this.currentPage = this.pagesCount;
    }
  }

  bindEvents(nativeElement: any) {
    nativeElement.addEventListener('touchstart', this.touchStartHandler, { passive: true });
    nativeElement.addEventListener('touchend', this.touchEndHandler, { passive: true });
  }

  prevHandler = () => {
    if (this.currentPage > 0) {
      this.currentPage--;
    }

    this.moduleId && this.moduleIndex && this.sendUpaService('left-arrow')
  }

  nextHandler = () => {
    if (this.currentPage < this.pagesCount) {
      this.currentPage++;
    }

    this.moduleId && this.moduleIndex && this.sendUpaService('right-arrow')
  }

  sendUpaService = (label: string) => {;
    this.upaService.trackClickTopic({
      label,
      moduleId: this.moduleId!,
      moduleIndex: this.moduleIndex!,
      target: UpaTarget.SLIDER,
      uniqueId: this.uniqueId
    });
  }

  touchStartHandler = (e: any) => {
    this.startClientX = e.touches[0].clientX;
    this.startClientY = e.touches[0].clientY;
  }

  touchEndHandler = (e: any) => {
    const endClientX = e.changedTouches[0].clientX;
    const endClientY = e.changedTouches[0].clientY;

    const isScrolled = Math.abs(this.startClientY - endClientY) > Math.abs(this.startClientX - endClientX);
    if (isScrolled) {
      return;
    }
    const action = this.startClientX > endClientX ? this.nextHandler : this.prevHandler;
    action();
  }
}
